import { Container, DateLabel, DatePickerAnchor, Header, HeaderCenter, SmallCard, CalendarButton } from "./styles";
import { AppointmentPickerProps } from "./types";
import { Button, Flex, Form } from "antd";
import { Loading } from "components/Loading";
import { CalendarBlank, CaretLeft, CaretRight } from "assets/icons";
import { Controller } from "react-hook-form";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import DatePickerWithAvailableDays from "components/DatePickerWithAvailableDays/DatePickerWithAvailableDays";
import spacing from "theme/spacing.module.scss";
import colors from "theme/colors.module.scss";
import dayjs, { Dayjs } from "dayjs";

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.05,
        },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
};

const slideVariants = {
    initial: (direction: number) => ({
        x: direction > 0 ? 50 : -50,
        opacity: 0,
    }),
    animate: {
        x: 0,
        opacity: 1,
        transition: { duration: 0.3 },
    },
    exit: (direction: number) => ({
        x: direction > 0 ? -50 : 50,
        opacity: 0,
        transition: { duration: 0.3 },
    }),
};

export default function DateWithTimePicker({
    date,
    setDate,
    slots = [],
    onSelect,
    name,
    control,
    setValue,
    loading = false,
    availableDays = [],
    onMonthChange,
    }: AppointmentPickerProps) {
    const today = new Date();
    const [direction, setDirection] = useState(0);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [pickerKey, setPickerKey] = useState(0);

    const changeDay = (offset: number) => {
        setDirection(offset);
        const newDate = new Date(date);
        newDate.setDate(newDate.getDate() + offset);
        setDate(newDate);
        setValue(name, null);
        setValue("date", dayjs(newDate).format("YYYY-MM-DD HH:mm"));
    };

    const handleOpenChange = (open: boolean) => {
        if (open) setPickerKey(k => k + 1);
        setIsDatePickerOpen(open);
    };

    const handleDatePickerChange = (dayjsDate: Dayjs | null) => {
        if (!dayjsDate) return;
        const now = new Date();
        const selected = dayjsDate.toDate();
        selected.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
        setDate(selected);
        setValue(name, null);
        setValue("date", dayjs(selected).format("YYYY-MM-DD HH:mm"));
    };

    return (
        <Container>
            <Form.Item>
                <Controller
                    name={name}
                    control={control}
                    render={({ field, fieldState: { error } }) => {
                        const handleSelect = (time: string) => {
                            field.onChange(time);
                            onSelect?.(date, time);
                        };

                        return (
                            <>
                                <Loading isLoading={loading}/>

                                <Header>
                                    <HeaderCenter>
                                        <Button
                                            onClick={() => changeDay(-1)}
                                            icon={<CaretLeft />}
                                            disabled={date <= today}
                                        />
                                        <AnimatePresence mode="wait" custom={direction}>
                                            <motion.div
                                                key={date.toISOString()}
                                                custom={direction}
                                                variants={slideVariants}
                                                initial="initial"
                                                animate="animate"
                                                exit="exit"
                                            >
                                                <DateLabel>
                                                    {date.toLocaleDateString("pl-PL", {
                                                        weekday: "short",
                                                        day: "numeric",
                                                        month: "long",
                                                        year: "numeric",
                                                    })}
                                                </DateLabel>
                                            </motion.div>
                                        </AnimatePresence>
                                        <Button
                                            onClick={() => changeDay(1)}
                                            icon={<CaretRight />}
                                        />
                                    </HeaderCenter>
                                    <DatePickerAnchor>
                                        <CalendarButton
                                            variant="solid"
                                            color="primary"
                                            onClick={() => setIsDatePickerOpen(true)}
                                            icon={<CalendarBlank height={24} width={24} />}
                                        />
                                        <DatePickerWithAvailableDays
                                            key={pickerKey}
                                            className="hidden-date-picker"
                                            open={isDatePickerOpen}
                                            onOpenChange={handleOpenChange}
                                            defaultPickerValue={dayjs(date)}
                                            onChange={handleDatePickerChange}
                                            availableDays={availableDays}
                                            onMonthChange={onMonthChange}
                                        />
                                    </DatePickerAnchor>
                                </Header>

                                <motion.div
                                    variants={containerVariants}
                                    initial="hidden"
                                    animate="visible"
                                >
                                    <Flex wrap gap={spacing.spacingXS} justify="space-evenly">
                                        {slots.length > 0 ? (
                                            <AnimatePresence mode="popLayout">
                                                {slots.map((slot) => (
                                                    <motion.div
                                                        key={slot}
                                                        variants={itemVariants}
                                                        initial="hidden"
                                                        animate="visible"
                                                        exit="hidden"
                                                        whileHover={{scale: 1.05}}
                                                        whileTap={{scale: 0.95}}
                                                    >
                                                        <Button
                                                            type={
                                                                field.value === slot
                                                                    ? "primary"
                                                                    : "default"
                                                            }
                                                            onClick={() => handleSelect(slot)}
                                                            disabled={loading}
                                                        >
                                                            {slot}
                                                        </Button>
                                                    </motion.div>
                                                ))}
                                            </AnimatePresence>
                                        ) : (
                                            !loading && (
                                                <motion.div
                                                    initial={{opacity: 0}}
                                                    animate={{opacity: 1}}
                                                >
                                                    <SmallCard>
                                                        Brak terminów w wybranym dniu. Wyszukaj ponownie.
                                                    </SmallCard>
                                                </motion.div>
                                            )
                                        )}
                                    </Flex>
                                </motion.div>

                                {error && (
                                    <span
                                        style={{
                                            color: colors.accentRed500,
                                        }}
                                    >
                                        {error.message}
                                    </span>
                                )}
                            </>
                        );
                    }}
                />
            </Form.Item>
        </Container>
    );
}
